import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../i18n/LanguageContext";

type WhyEgyptItem = {
  icon: "curriculum" | "home" | "arabic" | "affordable" | "support";
  title: string;
  description: string;
};

const WhyEgyptIcon: React.FC<{ variant: WhyEgyptItem["icon"] }> = ({ variant }) => {
  switch (variant) {
    case "curriculum":
      return (
        <svg viewBox="0 0 28 20" className="h-5 w-7" fill="none" aria-hidden="true">
          <path d="M4 4.5C4 3.67 4.67 3 5.5 3H13V17H5.5C4.67 17 4 16.33 4 15.5V4.5Z" fill="white" />
          <path d="M24 4.5C24 3.67 23.33 3 22.5 3H15V17H22.5C23.33 17 24 16.33 24 15.5V4.5Z" fill="white" />
          <path d="M13.5 3.5V16.5" stroke="#BFDB24" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      );
    case "home":
      return (
        <svg viewBox="0 0 28 20" className="h-5 w-7" fill="none" aria-hidden="true">
          <path d="M4 10.5L14 3L24 10.5V17H4V10.5Z" fill="white" />
          <path d="M10 17V11.8C10 11.35 10.35 11 10.8 11H17.2C17.65 11 18 11.35 18 11.8V17" fill="#BFDB24" />
        </svg>
      );
    case "arabic":
      return (
        <svg viewBox="0 0 28 20" className="h-5 w-7" fill="none" aria-hidden="true">
          <path d="M5 7.5C5 5.57 6.57 4 8.5 4H19.5C21.43 4 23 5.57 23 7.5V13C23 14.93 21.43 16.5 19.5 16.5H12.5L8 19V16.5H8.5C6.57 16.5 5 14.93 5 13V7.5Z" fill="white" />
          <circle cx="11" cy="9.5" r="1" fill="#BFDB24" />
          <circle cx="16" cy="9.5" r="1" fill="#BFDB24" />
        </svg>
      );
    case "affordable":
      return (
        <svg viewBox="0 0 28 20" className="h-5 w-7" fill="none" aria-hidden="true">
          <path d="M6 6.5C6 5.12 7.12 4 8.5 4H18.5L22 7.5V15.5C22 16.88 20.88 18 19.5 18H8.5C7.12 18 6 16.88 6 15.5V6.5Z" fill="white" />
          <path d="M13 8V14" stroke="#BFDB24" strokeWidth="1.7" strokeLinecap="round" />
          <path d="M11 10H15" stroke="#BFDB24" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      );
    case "support":
    default:
      return (
        <svg viewBox="0 0 28 20" className="h-5 w-7" fill="none" aria-hidden="true">
          <path d="M14 3L22 6V10C22 14.5 18.85 17.9 14 19C9.15 17.9 6 14.5 6 10V6L14 3Z" fill="white" />
          <path d="M10 10.2L12.8 13L18 7.8" stroke="#BFDB24" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
  }
};

const WhyEgyptSection: React.FC = () => {
  const { t, dir } = useLanguage();
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const isRtl = dir === "rtl";

  const content = (t as any)?.homeWhyEgypt || {};
  const items = (content.items || []) as WhyEgyptItem[];
  const displayItems = isRtl ? [...items].reverse() : items;

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    if (!("IntersectionObserver" in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -18% 0px", threshold: 0.2 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="overflow-hidden bg-white py-24 sm:py-28"
      aria-labelledby="why-egypt-title"
    >
      <div className="site-container flex flex-col gap-8">
        <div
          className={`flex flex-col gap-4 transition-all duration-700 ease-out md:flex-row md:items-start md:justify-between ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          } ${isRtl ? "md:flex-row-reverse md:text-right" : ""}`}
        >
          <h2
            id="why-egypt-title"
            className="header-title uppercase text-teal-900"
          >
            {content.title || "Why Egypt?"}
          </h2>
          <div
            className={`flex w-full max-w-[524px] flex-col gap-4 ${
              isRtl ? "items-end text-right" : "items-start text-left"
            }`}
          >
            <p className="section-subtitle text-neutral-600">
              {content.description ||
                "Egypt is one of the Arab world’s leading education destinations offering quality, accessibility, and globally recognized pathways for students."}
            </p>
            <Link
              to="/services"
              className="inline-flex min-h-10 w-full items-center justify-center gap-2.5 bg-second-color px-6 py-2 font-sans text-base font-medium leading-6 text-main-color transition-colors hover:bg-second-color/90 focus:outline-none focus:ring-2 focus:ring-main-color focus:ring-offset-2 focus:ring-offset-stone-100 sm:w-fit"
            >
              {content.cta || "Explore our Services"}
            </Link>
          </div>
        </div>

        <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6 ${
          isRtl ? "[direction:rtl] text-right" : "[direction:ltr]"
        }`}>
          {displayItems.map((item, index) => {
            const isFirstRow = index < 3;
            const spanClass = isFirstRow ? "xl:col-span-2" : "xl:col-span-3";
            const delay = `${index * 110}ms`;

            return (
              <article
                key={item.title}
                className={`group flex h-56 flex-col justify-between overflow-hidden bg-main-color/20 p-6 transition-all duration-700 ease-out hover:-translate-y-1 ${spanClass} ${
                  isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
                } ${isRtl ? "text-right items-end" : "text-left items-start"}`}
                style={{ transitionDelay: delay }}
              >
                <div
                  className={`flex h-10 w-10 items-center justify-center overflow-hidden bg-main-color ${
                    isRtl ? "self-end" : "self-start"
                  }`}
                >
                  <WhyEgyptIcon variant={item.icon} />
                </div>

                <div className={`flex w-full max-w-80 flex-col gap-1 ${
                  isRtl ? "items-end text-right" : "items-start text-left"
                }`}>
                  <h3 className="font-sans text-xl font-semibold leading-6 text-teal-900 ">
                    {item.title}
                  </h3>
                  <p className="font-sans text-sm font-normal leading-4 text-teal-900">
                    {item.description}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyEgyptSection;
